/* Copyright (c) 2026, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { Directive, ElementRef, OnInit, input } from '@angular/core';

/**
 * Makes heading elements programmatically focusable for screen reader navigation.
 *
 * Problem: When content is inside a scrollable container (e.g., a div with overflow-y: auto)
 * rather than the document body, screen readers like Narrator can navigate to headings
 * using the "H" key, but the browser won't automatically scroll the container to bring
 * the heading into view because non-interactive elements are not focusable by default.
 *
 * Solution: Adding tabindex="-1" makes headings programmatically focusable. When the
 * screen reader navigates to a heading, it can trigger focus(), which causes the
 * browser to call scrollIntoView() on the scrollable parent container.
 *
 * This applies to scrollable layouts like:
 * - Layout's marquee-game-section-container (overflow-y: auto)
 * - Game grid's mat-sidenav-content
 * - Settings pages and dialogs with scrollable content
 * - Any component using cdk-virtual-scroll-viewport
 *
 * This directive automatically applies to:
 * - Elements with role="heading" (e.g., <span role="heading" aria-level="2">)
 * - Native heading elements (<h1> through <h6>)
 *
 * Optional inputs:
 * - [nvHeadingFocusable]: A single heading level or list of levels to opt into.
 *   When provided, the directive only applies to headings matching those levels.
 *   If used with a single level, role="heading" and aria-level are set for you.
 * - [nvHeadingFocusableTabindex]: Overrides the tabindex value (defaults to -1).
 *
 * Note: tabindex="-1" means:
 * - Element CAN be focused programmatically (via element.focus() or screen reader)
 * - Element is NOT in the Tab key sequence (users won't Tab to headings)
 * - Spatial navigation (TV/10ft) ignores tabindex="-1" elements
 */
// eslint-disable-next-line @angular-eslint/directive-selector -- Targets native heading elements and ARIA roles
@Directive({
    selector: '[nvHeadingFocusable], [role="heading"], h1, h2, h3, h4, h5, h6',
    standalone: true
})
export class HeadingFocusableDirective implements OnInit {
    // Optional opt-in for heading level(s); empty means "apply to any heading."
    readonly nvHeadingFocusable = input<readonly number[] | number | null>(null);
    // Optional override for tabindex to align with specific focus behavior.
    readonly nvHeadingFocusableTabindex = input<number | null>(null);

    constructor(private el: ElementRef<HTMLElement>) {}

    ngOnInit() {
        const levels = this.normalizeLevels(this.nvHeadingFocusable());
        const headingLevel = this.getHeadingLevel();

        // Skip when the element's heading level does not match allowed levels.
        if (!this.checkIsAllowedTagLevel(headingLevel, levels)) {
            return;
        }

        // If a single level is present on a non-heading element, mark it as a heading.
        if (levels.length === 1 && !this.isNativeHeading()) {
            this.el.nativeElement.setAttribute('role', 'heading');
            this.el.nativeElement.setAttribute('aria-level', String(levels[0]));
        }

        const tabindexValue = this.nvHeadingFocusableTabindex() ?? -1;

        // Only set tabindex if not already explicitly defined in the template.
        if (!this.el.nativeElement.hasAttribute('tabindex')) {
            this.el.nativeElement.setAttribute('tabindex', String(tabindexValue));
        }
    }

    private getHeadingLevel(): number | null {
        const tagName = this.el.nativeElement.tagName.toLowerCase();
        if (tagName.startsWith('h')) {
            // Native heading tags (h1-h6) set level by tag name.
            const parsedTagName = Number(tagName.slice(1));
            if (Number.isInteger(parsedTagName) && parsedTagName >= 1 && parsedTagName <= 6) {
                return parsedTagName;
            }
        }

        if (this.el.nativeElement.getAttribute('role') === 'heading') {
            // ARIA heading level is expected when role="heading" is present.
            const ariaLevel = this.el.nativeElement.getAttribute('aria-level');
            const parsedAriaLevel = Number(ariaLevel);
            if (Number.isInteger(parsedAriaLevel) && parsedAriaLevel >= 1 && parsedAriaLevel <= 6) {
                return parsedAriaLevel;
            }
        }

        return null;
    }

    private isNativeHeading(): boolean {
        const tagName = this.el.nativeElement.tagName.toLowerCase();
        return (
            tagName === 'h1' ||
            tagName === 'h2' ||
            tagName === 'h3' ||
            tagName === 'h4' ||
            tagName === 'h5' ||
            tagName === 'h6'
        );
    }

    private normalizeLevels(levels: readonly number[] | number | null): number[] {
        // Normalize to unique, valid heading levels only.
        const validHeadingLevels = (Array.isArray(levels) ? levels : levels === null ? [] : [levels])
            .map(level => Number(level))
            .filter(level => Number.isInteger(level) && level >= 1 && level <= 6);

        return [...new Set(validHeadingLevels)];
    }

    private checkIsAllowedTagLevel(level: number | null, allowedLevels: number[]): boolean {
        if (allowedLevels.length === 0) {
            return true;
        }

        if (level === null) {
            // Without an existing level, only allow a single explicit level.
            return allowedLevels.length === 1;
        }

        return allowedLevels.includes(level);
    }
}
